import { BatteryMedium, Car, Flame, Home, PlugZap, Sun, UtilityPole } from 'lucide-react';
import type { ComponentType, CSSProperties, SVGProps } from 'react';
import { formatPercent, formatPower, type SiteLive } from '@ste/shared';
import { t } from '../i18n/index.ts';
import { cx } from './ui.tsx';

// Live energy flow (ADR-0028): PV, grid and battery feed the house in the middle, which
// supplies wallboxes and heat pump below. Raised discs, dots move in the direction of the
// energy. Green = own energy, warm = grid import (never red).

const ACTIVE_W = 30;
const WIDTH = 340;
const HUB = { x: 170, y: 132, r: 46 };
const NODE_R = 31;
const PV = { x: 170, y: 36 };
const SIDE_Y = 118;
const CONSUMER_Y = 236;

type Icon = ComponentType<SVGProps<SVGSVGElement> & { size?: number | string }>;

interface Point {
  x: number;
  y: number;
}

/** straight line between two discs, cut at their edges */
function edgeLine(a: Point, ra: number, b: Point, rb: number): [Point, Point] {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const length = Math.hypot(dx, dy) || 1;
  const ux = dx / length;
  const uy = dy / length;
  return [
    { x: a.x + ux * ra, y: a.y + uy * ra },
    { x: b.x - ux * rb, y: b.y - uy * rb },
  ];
}

/** a line from `from` to `to`; with power the dots run along it (reverse: against it) */
function Flow({
  from,
  to,
  power,
  color,
  reverse,
}: {
  from: Point;
  to: Point;
  power: number;
  color: string;
  reverse?: boolean;
}) {
  const active = Math.abs(power) >= ACTIVE_W;
  const [a, b] = reverse ? [to, from] : [from, to];
  return (
    <g>
      <line
        x1={a.x}
        y1={a.y}
        x2={b.x}
        y2={b.y}
        stroke={active ? color : 'var(--border)'}
        strokeOpacity={active ? 0.35 : 1}
        strokeWidth={3}
        strokeLinecap="round"
        strokeDasharray={active ? undefined : '2 7'}
      />
      {active && (
        <line
          x1={a.x}
          y1={a.y}
          x2={b.x}
          y2={b.y}
          stroke={color}
          strokeWidth={7}
          strokeLinecap="round"
          className="flow-dots"
        />
      )}
    </g>
  );
}

function position(point: Point, height: number): CSSProperties {
  return { left: `${(point.x / WIDTH) * 100}%`, top: `${(point.y / height) * 100}%` };
}

/** raised disc with a pressed-in core; active cores glow in their color */
function Disc({
  icon: IconComponent,
  color,
  active,
  big = false,
  children,
}: {
  icon: Icon;
  color: string;
  active: boolean;
  big?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <div
      className={cx(
        'grid shrink-0 place-items-center rounded-full bg-surface shadow-raised-sm',
        big ? 'h-[92px] w-[92px]' : 'h-[62px] w-[62px]',
      )}
    >
      <div
        className={cx(
          'flex flex-col items-center justify-center rounded-full',
          big ? 'h-[72px] w-[72px]' : 'h-[46px] w-[46px]',
          !active && 'bg-surface-2 text-muted shadow-inset',
        )}
        style={
          active
            ? {
                color: 'var(--on-accent)',
                background: `radial-gradient(circle at 35% 30%, color-mix(in srgb, ${color} 55%, white), ${color})`,
                boxShadow: `inset -3px -4px 8px rgb(0 0 0 / 0.16), 0 0 18px color-mix(in srgb, ${color} 40%, transparent)`,
              }
            : undefined
        }
      >
        <IconComponent width={big ? 24 : 22} height={big ? 24 : 22} aria-hidden />
        {children}
      </div>
    </div>
  );
}

function Value({ value, label }: { value: string; label: string }) {
  return (
    <div className="text-center leading-tight whitespace-nowrap">
      <div className="text-[15px] font-bold text-text">{value}</div>
      <div className="mt-0.5 max-w-[7.5rem] truncate text-xs text-muted">{label}</div>
    </div>
  );
}

export function EnergyFlow({ live }: { live: SiteLive }) {
  const pv = live.pv.power;
  const grid = live.grid.power;
  const battery = live.battery?.power ?? 0;
  const home = live.home.power;
  const gridColor = grid > 0 ? 'var(--warm)' : 'var(--accent)';
  const consumers = live.loadpoints.map((lp) => ({
    key: `lp${lp.id}`,
    icon: lp.kind === 'heatpump' ? Flame : lp.charging ? PlugZap : Car,
    label: lp.title,
    power: lp.power,
    color: lp.kind === 'heatpump' ? 'var(--heat)' : 'var(--accent)',
  }));
  const height = consumers.length > 0 ? 300 : 200;
  const spacing = WIDTH / (consumers.length + 1);
  const gridPoint = { x: 48, y: SIDE_Y };
  const batteryPoint = { x: WIDTH - 48, y: SIDE_Y };
  const [pvA, pvB] = edgeLine(PV, NODE_R, HUB, HUB.r);
  const [gridA, gridB] = edgeLine(gridPoint, NODE_R, HUB, HUB.r);
  const [batA, batB] = edgeLine(batteryPoint, NODE_R, HUB, HUB.r);

  return (
    <div
      role="img"
      aria-label="Energiefluss"
      className="relative mx-auto w-full max-w-[440px] tabular-nums"
      style={{ aspectRatio: `${WIDTH} / ${height}` }}
    >
      <svg viewBox={`0 0 ${WIDTH} ${height}`} className="absolute inset-0 h-full w-full" aria-hidden>
        <Flow from={pvA} to={pvB} power={pv} color="var(--accent)" />
        <Flow from={gridA} to={gridB} power={grid} color={gridColor} reverse={grid < 0} />
        {live.battery && (
          <Flow from={batA} to={batB} power={battery} color="var(--battery)" reverse={battery < 0} />
        )}
        {consumers.map((c, i) => {
          const [a, b] = edgeLine(HUB, HUB.r, { x: spacing * (i + 1), y: CONSUMER_Y }, NODE_R);
          return <Flow key={c.key} from={a} to={b} power={c.power} color={c.color} />;
        })}
      </svg>

      {/* PV: value beside the disc, the line runs below it */}
      <div
        className="absolute flex items-center gap-2.5"
        style={{ ...position(PV, height), transform: `translate(-${NODE_R}px, -50%)` }}
      >
        <Disc icon={Sun} color="var(--accent)" active={pv >= ACTIVE_W} />
        <div className="leading-tight whitespace-nowrap">
          <div className="text-[15px] font-bold text-text">{formatPower(pv)}</div>
          <div className="mt-0.5 text-xs text-muted">{t('flow.pv')}</div>
        </div>
      </div>

      <Node point={gridPoint} height={height}>
        <Disc icon={UtilityPole} color={gridColor} active={Math.abs(grid) >= ACTIVE_W} />
        <Value
          value={formatPower(Math.abs(grid))}
          label={
            Math.abs(grid) < ACTIVE_W
              ? t('flow.grid')
              : `${t('flow.grid')} · ${grid > 0 ? t('flow.import') : t('flow.export')}`
          }
        />
      </Node>

      {live.battery && (
        <Node point={batteryPoint} height={height}>
          <Disc icon={BatteryMedium} color="var(--battery)" active={Math.abs(battery) >= ACTIVE_W} />
          <Value
            value={formatPower(Math.abs(battery))}
            label={`${formatPercent(live.battery.soc)} · ${
              Math.abs(battery) < ACTIVE_W
                ? t('flow.idle')
                : battery < 0
                  ? t('flow.charging')
                  : t('flow.discharging')
            }`}
          />
        </Node>
      )}

      {/* the house is the hub: its consumption sits inside the disc */}
      <Node point={HUB} height={height} center>
        <Disc icon={Home} color="var(--accent)" active={false} big>
          <span className="mt-0.5 text-[13px] leading-none font-bold whitespace-nowrap text-text">
            {formatPower(home)}
          </span>
        </Disc>
        <span className="sr-only">{t('flow.home')}</span>
      </Node>

      {consumers.map((c, i) => (
        <Node key={c.key} point={{ x: spacing * (i + 1), y: CONSUMER_Y }} height={height}>
          <Disc icon={c.icon} color={c.color} active={c.power >= ACTIVE_W} />
          <Value value={formatPower(c.power)} label={c.label} />
        </Node>
      ))}
    </div>
  );
}

/** positions a disc with its centre on `point`; the value hangs below it */
function Node({
  point,
  height,
  center = false,
  children,
}: {
  point: Point;
  height: number;
  center?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div
      className="absolute flex flex-col items-center gap-1.5"
      style={{
        ...position(point, height),
        transform: center ? 'translate(-50%, -50%)' : `translate(-50%, -${NODE_R}px)`,
      }}
    >
      {children}
    </div>
  );
}
